import { FC, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
import { P1, P2 } from "./Paragraph";
import { color as themeColor } from "@styles";
import { Image } from "./Image";
import { Spacer } from "../templates/Spacer";
import { StandardSection } from "../templates/StandardSection";
import { BetterButton } from "./BetterButton";
import { useLocale } from "@hooks";

export interface RegionalReward {
  _id: string;
  heading: string;
  description: string;
  imageAlt: string;
  image: string;
}

export enum PartnerType {
  reward = "Our reward partners",
  wellbeing = "Our wellbeing partners",
}

interface RewardCardGridProps {
  rewards: RegionalReward[];
  pt?: string;
  pb?: string;
  pl?: string;
  pr?: string;
}

const PAGE_SIZE = 9;

export const RewardCardGrid: FC<React.PropsWithChildren<RewardCardGridProps>> = ({
  rewards,
  pt,
  pb,
  pl,
  pr,
}) => {
  const [allPagesVisible, setAllPagesVisible] = useState(rewards.length <= PAGE_SIZE);
  const { locale } = useLocale();
  const buttonText = locale === "jp" ? "すべてを表示" : "See all rewards";
  const visibleRewards = useMemo(() => {
    if (!Array.isArray(rewards)) {
      return [];
    }

    return rewards.slice(0, allPagesVisible ? rewards.length : PAGE_SIZE);
  }, [rewards, allPagesVisible]);

  const handleClick = useCallback(() => {
    setAllPagesVisible(true);
  }, []);

  if (!visibleRewards || !Array.isArray(visibleRewards) || visibleRewards?.length < 1) {
    return null;
  }

  return (
    <StandardSection pl={pl} pr={pr} pt={pt} pb={pb}>
      <Grid>
        {visibleRewards.map((reward, index) => (
          <div key={reward.heading}>
            {index === visibleRewards.length - 1 && visibleRewards.length % 3 === 1 ? (
              <div />
            ) : null}
            <RewardCard {...reward} />
          </div>
        ))}
      </Grid>
      {allPagesVisible ? null : (
        <ButtonWrapper>
          <BetterButton onClick={handleClick} text={buttonText} variant="purpleInverted" />
        </ButtonWrapper>
      )}
    </StandardSection>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(16rem, 1fr));
  grid-gap: 2rem;
  width: 100%;
  max-width: 66rem;
  margin: 0 auto;

  @media only screen and (max-width: 30rem) {
    grid-template-columns: repeat(auto-fill, minmax(100%, 1fr));
  }
`;

export const RewardCard: FC<React.PropsWithChildren<RegionalReward>> = ({
  image,
  imageAlt,
  heading,
  description,
}) => {
  return (
    <Card aria-label={heading}>
      <ImageContainer>
        <Image path={image} alt={imageAlt} />
      </ImageContainer>
      <hr style={{ borderTop: "0.125rem solid #D9D9D7", margin: 0 }} />
      <TextContainer>
        <P1 style={{ padding: 0, lineHeight: "1.6rem" }}>
          <Strong>{heading}</Strong>
        </P1>
        <Spacer height="1.5rem" />
        <P2 color="#956AFF" style={{ padding: 0 }}>
          {description}
        </P2>
      </TextContainer>
    </Card>
  );
};

const Card = styled.div`
  background: #fafafe;
  border: 0.125rem solid #d9d9d7;
  box-sizing: border-box;
  box-shadow: 0.5rem 0.5rem 0px #eae1ff;
  border-radius: 0.5rem;
  height: 100%;
`;

const ImageContainer = styled.div`
  padding: 0 2rem;
  margin: 0 auto;
  background: #fff;
  border-radius: 0.5rem 0.5rem 0 0;
  pointer-events: none;
  display: flex;
  justify-content: center;

  img {
    width: 18.875rem;
    height: auto;
    max-width: 100%;
  }
`;

const Strong = styled.strong`
  display: inline-block;
  color: #640038;
  letter-spacing: 0.075rem;
  padding: 0;

  ::-moz-selection {
    background: ${themeColor.P20};
  }
  ::selection {
    background: ${themeColor.P20};
  }
`;

const TextContainer = styled.div`
  padding: 2rem;
  word-wrap: break-word;
  white-space: pre-line;
  text-align: left;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 6rem;
  padding-bottom: 9rem;
`;
